import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Tooltip } from 'radix-ui';
import { createBrowserRouter, Navigate, Outlet, RouterProvider } from 'react-router';
import { NetworkError, StaleResponseError } from '../lib/api';
import { SupportForm } from '../support/SupportForm';
import { TicketsPage, TicketPage } from '../support/Tickets';
import { InstallPage } from '../install/InstallPage';
import { AccountLinkPage } from '../session/AccountLink';
import { AskPage } from '../pages/AskPage';
import { CustomerPage, SitePage } from '../pages/CustomerPage';
import { CustomersPage } from '../pages/CustomersPage';
import { EndUsersPage } from '../pages/EndUsersPage';
import { DevicePage } from '../pages/DevicePage';
import { DevicesPage, SearchPage } from '../pages/DevicesPage';
import { NotFoundPage } from '../pages/NotFound';
import { SettingsPage } from '../pages/SettingsPage';
import { TodayPage } from '../pages/TodayPage';
import { AuditPage } from '../pages/AuditPage';
import { ScriptLibraryPage } from '../pages/ScriptLibraryPage';
import { WorkPage } from '../pages/WorkPage';
import { TerminalPage } from '../remote/TerminalPage';
import { RemoteViewerPage } from '../remote/ViewerPage';
import { BareScopeLayout, ChooseWorkspace, ScopeLayout } from '../scope/scope';
import { SessionGate } from '../session/session';
import { ToastProvider } from '../ui/toast';

export function createQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // Never retry a refusal; retry a dropped connection or a response discarded by a scope change once.
        retry: (count, error) => count < 1 && (error instanceof NetworkError || error instanceof StaleResponseError),
        refetchOnWindowFocus: true,
        throwOnError: false,
      },
    },
  });
}

export const routes = [
  // An installer download link (WRMM-81), opened by the person at the computer to set up: no sign-in.
  { path: 'install/:id', element: <InstallPage /> },
  // An emailed account setup or password-reset link (WRMM-134): no sign-in; the token is in the fragment.
  { path: 'setup', element: <AccountLinkPage /> },
  { path: 'reset-password', element: <AccountLinkPage /> },
  { path: 'support', element: <SupportForm /> },
  {
    element: <SessionGate><Outlet /></SessionGate>,
    children: [
      { index: true, element: <ChooseWorkspace /> },
      {
        path: 'w/:mspId', element: <ScopeLayout />,
        children: [
          { index: true, element: <Navigate to="today" replace /> },
          { path: 'today', element: <TodayPage /> },
          { path: 'devices', element: <DevicesPage /> },
          { path: 'devices/:id', element: <DevicePage /> },
          { path: 'customers', element: <CustomersPage /> },
          { path: 'customers/:id', element: <CustomerPage /> },
          { path: 'end-users', element: <EndUsersPage /> },
          { path: 'sites/:id', element: <SitePage /> },
          { path: 'search', element: <SearchPage /> },
          { path: 'work', element: <WorkPage /> },
          { path: 'tickets', element: <TicketsPage /> },
          { path: 'tickets/:id', element: <TicketPage /> },
          { path: 'audit', element: <AuditPage /> },
          { path: 'scripts', element: <ScriptLibraryPage /> },
          { path: 'ask', element: <AskPage /> },
          { path: 'settings', element: <SettingsPage /> },
          { path: '*', element: <NotFoundPage /> },
        ],
      },
      // The remote desktop viewer, in a window of its own without the console around it.
      { path: 'w/:mspId/remote', element: <BareScopeLayout />, children: [{ path: ':deviceId', element: <RemoteViewerPage /> }] },
      { path: 'w/:mspId/terminal', element: <BareScopeLayout />, children: [{ path: ':deviceId', element: <TerminalPage /> }] },
      { path: '*', element: <ChooseWorkspace /> },
    ],
  },
];

export function Providers({ client, children }: { client: QueryClient; children: React.ReactNode }) {
  return (
    <QueryClientProvider client={client}>
      <Tooltip.Provider>
        <ToastProvider>{children}</ToastProvider>
      </Tooltip.Provider>
    </QueryClientProvider>
  );
}

export function App() {
  const client = createQueryClient();
  const router = createBrowserRouter(routes);
  return <Providers client={client}><RouterProvider router={router} /></Providers>;
}

